import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdCard from "@/components/AdCard";
import { useAuth } from "@/context/AuthContext";
import { PenSquare, Inbox, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ExpirationBanner({ ad, onRenewed }) {
  const [busy, setBusy] = useState(false);
  const remaining = daysLeft(ad.expires_at);
  if (remaining === null) return null;

  const expired = remaining <= 0;
  const soon = remaining > 0 && remaining <= 2;

  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ads/${ad.id}/renew`);
      toast.success("Anuncio renovado 7 días más");
      onRenewed?.(data);
    } catch {
      toast.error("No se pudo renovar el anuncio");
    } finally {
      setBusy(false);
    }
  };

  let tone;
  let label;
  if (expired) {
    tone = "border-[#B85C3D] bg-[#FBEEE4] text-[#7A3A22]";
    label = "Caducado — renuévalo para volver a mostrarlo";
  } else if (soon) {
    tone = "border-[#D4B78A] bg-[#F5EFE6] text-[#7A5A28]";
    label = `Caduca en ${remaining} día${remaining === 1 ? "" : "s"}`;
  } else {
    tone = "border-[#A3B8DA] bg-[#EEF4FB] text-[#1E3A8A]";
    label = `Activo · ${remaining} días restantes`;
  }

  return (
    <div className={`border ${tone} px-3 py-2 mt-2 flex items-center justify-between gap-2 text-xs`}
      data-testid={`ad-expiration-${ad.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate font-medium">{label}</span>
      </div>
      {(expired || soon) && (
        <button
          onClick={renew}
          disabled={busy}
          data-testid={`ad-renew-${ad.id}`}
          className="inline-flex items-center gap-1.5 bg-[#1E3A8A] text-[#F8FAFC] px-3 h-8 font-semibold text-xs hover:bg-[#152B6B] disabled:opacity-50 flex-shrink-0"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          Renovar
        </button>
      )}
    </div>
  );
}

export default function MyAdsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !(user && user.id)) {
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && user.id) {
      api.get("/ads/mine").then((r) => setAds(r.data)).finally(() => setLoading(false));
    }
  }, [user]);

  const onRenewed = (updated) => {
    setAds((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  if (authLoading || !(user && user.id)) return null;

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-2">Panel personal</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1A1A1A]">Mis anuncios</h1>
            <p className="text-sm text-[#475569] mt-2 max-w-xl">
              Los anuncios están activos durante 7 días. Cuando estén cerca de caducar podrás renovarlos con un clic.
            </p>
          </div>
          <Link
            to="/publicar"
            data-testid="my-ads-publish-button"
            className="inline-flex items-center gap-2 h-11 px-5 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] font-medium"
          >
            <PenSquare className="w-4 h-4" /> Nuevo anuncio
          </Link>
        </div>

        {loading ? (
          <p className="text-[#475569]">Cargando...</p>
        ) : ads.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] p-14 text-center">
            <Inbox className="w-10 h-10 mx-auto text-[#3B5FB8] mb-4" />
            <h3 className="font-serif text-2xl font-semibold text-[#1A1A1A] mb-2">Aún no has publicado nada</h3>
            <p className="text-[#475569] mb-6">Empieza publicando tu primer anuncio.</p>
            <Link to="/publicar" className="inline-flex items-center gap-2 h-11 px-6 bg-[#1A1A1A] text-[#F8FAFC] font-medium">
              Crear anuncio
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ads.map((ad) => (
              <div key={ad.id} className="flex flex-col">
                <AdCard ad={ad} />
                <ExpirationBanner ad={ad} onRenewed={onRenewed} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
